import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  AiOutlineHome,
  AiOutlinePicture,
  AiOutlineTeam,
  AiOutlineLogout,
  AiOutlineApi,
  AiOutlineHistory,
} from 'react-icons/ai';
import { TbTrash } from 'react-icons/tb';
import BottomNav from './BottomNav';
import * as uploads from '../lib/uploadQueue';
import './AppLayout.css';

const AppLayout = ({ user, onLogout, onOpenCamera, children }) => {
  // Uploads continue after the camera closes, so they need somewhere visible to
  // live. Without this the only honest answer to "did my photos go?" was to
  // reopen the camera.
  const [pending, setPending] = useState(0);
  useEffect(() => uploads.subscribe((items) => {
    setPending(items.filter((i) => i.status === 'pending' || i.status === 'uploading').length);
  }), []);

  const displayName = user?.name || user?.email || 'User';
  const displayEmail = user?.email || '';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <div className="app-layout">
      {/* Mobile top bar — compact, icon-only mark (BottomNav already handles navigation) */}
      <div className="mobile-topbar">
        <img src="/logo-mark.png" alt="Capturify" className="mobile-brand-mark" />
      </div>

      {/* Visible on every screen while photos are still going up, so a technician
          can see the count fall to zero instead of guessing. */}
      {pending > 0 && (
        <div className="upload-pill" role="status" aria-live="polite">
          <span className="upload-pill-dot" />
          Uploading {pending} photo{pending !== 1 ? 's' : ''}
        </div>
      )}

      {/* Sidebar (desktop only — mobile uses the bottom tab bar instead) */}
      <aside className="sidebar">
        {/* Brand */}
        <div className="sidebar-brand">
          <img src="/logo.png" alt="Capturify by ChimneySweeps" className="brand-logo-full" />
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `nav-item${isActive ? ' nav-item--active' : ''}`
            }
          >
            <AiOutlineHome size={17} className="nav-icon" />
            Dashboard
          </NavLink>

          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `nav-item${isActive ? ' nav-item--active' : ''}`
            }
          >
            <AiOutlinePicture size={17} className="nav-icon" />
            Projects
          </NavLink>

          {(user?.isAdmin || user?.isManager) && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `nav-item${isActive ? ' nav-item--active' : ''}`
              }
            >
              <AiOutlineTeam size={17} className="nav-icon" />
              User Management
            </NavLink>
          )}

          {(user?.isAdmin || user?.isManager) && (
            <NavLink
              to="/removed-photos"
              className={({ isActive }) =>
                `nav-item${isActive ? ' nav-item--active' : ''}`
              }
            >
              <TbTrash size={17} className="nav-icon" />
              Removed Photos
            </NavLink>
          )}

          {(user?.isAdmin || user?.isManager) && (
            <NavLink
              to="/activity"
              className={({ isActive }) =>
                `nav-item${isActive ? ' nav-item--active' : ''}`
              }
            >
              <AiOutlineHistory size={17} className="nav-icon" />
              Activity
            </NavLink>
          )}

          {user?.isAdmin && (
            <NavLink
              to="/integrations"
              className={({ isActive }) =>
                `nav-item${isActive ? ' nav-item--active' : ''}`
              }
            >
              <AiOutlineApi size={17} className="nav-icon" />
              Integrations
            </NavLink>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{avatarLetter}</div>
            <div className="user-info">
              <span className="user-name">{displayName}</span>
              <span className="user-email">{displayEmail}</span>
            </div>
          </div>
          <button className="sidebar-logout-btn" onClick={onLogout}>
            <AiOutlineLogout size={15} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {children}
      </main>

      <BottomNav onOpenCamera={onOpenCamera} />
    </div>
  );
};

export default AppLayout;
